import React from 'react';
import { useTranslation } from 'react-i18next';
import { Pagination } from 'antd';

export interface CommissionRecord {
  id: string;
  code: string;
  partnerName: string;
  referrer: string;
  contractCode: string;
  investAmount: number;
  commissionRate: number;
  commissionAmount: number;
  status: string;
}

interface MembersCommissionTabProps {
  stats?: {
    totalCommission: number;
    totalPaid: number;
    totalPending?: number;
    totalRejected?: number;
  };
  records?: CommissionRecord[];
  currentPage?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
}

const MembersCommissionTab: React.FC<MembersCommissionTabProps> = ({ 
  stats: propStats, 
  records: propRecords,
  currentPage = 1,
  totalItems = 0,
  onPageChange
}) => {
  const { t } = useTranslation();
  // console.log('MembersCommissionTab props:', { propStats, propRecords });
  // Mock data
  const defaultStats = {
    totalCommission: 0,
    totalPaid: 0,
    totalPending: 0,
  };

  const stats = propStats || defaultStats;
  const records = propRecords || [];


  const formatCurrency = (value: number) => {
    return value.toLocaleString('vi-VN', { maximumFractionDigits: 0 }) + ' đ';
  };

  const getStatusBadge = (status: CommissionRecord['status']) => {
    switch (status) {
      case 'pending':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
            {t('investorDetail.commission.pending', 'Chờ duyệt')}
          </span>
        );
      case 'active':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
            {t('investorDetail.commission.active', 'Hoạt động')}
          </span>
        );
      case 'paid':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
            {t('investorDetail.commission.paid', 'Đã chi')}
          </span>
        );
      case 'rejected':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
            {t('investorDetail.commission.rejected', 'Từ chối')}
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
            {t('investorDetail.commission.totalCommission', 'Tổng hoa hồng')}
          </p>
          <p className="text-lg font-semibold text-gray-900 dark:text-white">
            {formatCurrency(stats.totalCommission)}
          </p>
        </div>

        <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
            {t('investorDetail.commission.totalPaid', 'Đã chi trả')}
          </p>
          <p className="text-lg font-semibold text-green-600 dark:text-green-400">
            {formatCurrency(stats.totalPaid)}
          </p>
        </div>

        <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
            {t('investorDetail.commission.totalPending', 'Chờ duyệt')} / {t('investorDetail.commission.totalRejected', 'Từ chối')}
          </p>
          <p className="text-lg font-semibold text-yellow-600 dark:text-yellow-400">
            {formatCurrency((stats.totalPending || 0))}
          </p>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('investorDetail.commission.code', 'Mã ĐT')}
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('investorDetail.commission.partner', 'Đối tác')}
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('investorDetail.commission.referrer', 'NĐT giới thiệu')}
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('investorDetail.commission.contract', 'Khoản ĐT')}
              </th>
              <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('investorDetail.commission.investAmount', 'Số tiền ĐT')}
              </th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                % HH
              </th>
              <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('investorDetail.commission.commissionAmount', 'Hoa hồng thực nhận')}
              </th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('investorDetail.commission.status', 'Trạng thái')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {records.map((record) => (
              <tr key={record.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td
                  className="px-3 py-3 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap"
                  title={record.code}
                >
                  {record.code.length > 15 ? record.code.slice(0, 15) + '...' : record.code}
                </td>
                <td className="px-3 py-3 whitespace-nowrap">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {record.partnerName}
                    </p>
                  </div>
                </td>
                <td className="px-3 py-3 text-sm text-blue-600 dark:text-blue-400 whitespace-nowrap">
                  {record.referrer}
                </td>
                <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                  {record.contractCode}
                </td>
                <td className="px-3 py-3 text-sm text-right text-gray-900 dark:text-white whitespace-nowrap">
                  {formatCurrency(record.investAmount)}
                </td>
                <td className="px-3 py-3 text-sm text-center text-gray-600 dark:text-gray-300 whitespace-nowrap">
                  {record.commissionRate}%
                </td>
                <td className="px-3 py-3 text-sm text-right font-medium text-blue-600 dark:text-blue-400 whitespace-nowrap">
                  {formatCurrency(record.commissionAmount)}
                </td>
                <td className="px-3 py-3 text-center whitespace-nowrap">
                  {getStatusBadge(record.status)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalItems > 0 && onPageChange && (
        <div className="flex justify-end pt-4">
          <Pagination
            current={currentPage}
            total={totalItems}
            pageSize={10}
            onChange={onPageChange}
            showSizeChanger={false}
          />
        </div>
      )}
    </div>
  );
};

export default MembersCommissionTab;
